export interface HelpText {
  title: string;
  body: string;
  items?: { label: string; text: string }[];
}

export const fieldHelp = {
  owner: 'Ansvarar för planen och kan ändra datum, omfattning och status.',
  announcement: 'Dagen kedjan aviseras. Den blir vecka 0 och deadlines räknas bakåt från den.',
  roles: 'Koppla mallens roller till personer. Uppgifter för rollen tilldelas personen direkt.',
  roleFilter: 'Rollen från mallen, till exempel Head of Inhouse. Person är den som faktiskt är tilldelad.',
  occurredAt: 'När det faktiskt hände. Ändra datumet om du registrerar i efterhand.',
  decisionRole: 'Rollen du fattar beslutet för. Bara roller som uppgiften kräver visas.',
  lateRequestedBy: 'Vem som bad om ändringen.',
  lateImpact: 'Vad som påverkas, till exempel tryck eller säljmaterial.',
  lateRework: 'Till exempel "2 dagar Inhouse".',
  lateOccurredAt: 'Dagen ändringen kom in.',
} as const;

export const lifecycleHelp: Record<string, string> = {
  activate: 'Startar lanseringen. Uppgifterna syns sedan i Mitt arbete.',
  pause: 'Markerar att arbetet står still. Uppgifterna går fortfarande att ändra.',
  resume: 'Tar bort pausen och arbetet fortsätter.',
  markLaunched: 'Produkten är ute. Lanseringen kan fortfarande avslutas senare.',
  close: 'Arkiverar lanseringen. Den blir skrivskyddad och kan inte ändras.',
  cancel: 'Lanseringen blir inte av. Orsaken sparas och den blir skrivskyddad.',
};

export const help = {
  listCards: {
    title: 'Så läser du korten',
    body: 'Varje kort är en lansering. Ringen visar hur stor andel av uppgifterna som är klara. Etiketten visar läget just nu.',
    items: [
      { label: 'ICA · v.43', text: 'Vecka för kedjans avisering.' },
      { label: 'I fas', text: 'Inget är försenat eller riskerar att bli sent.' },
    ],
  },
  myWorkIntro: {
    title: 'Mitt arbete',
    body: 'Uppgifter för dina avdelningar i aktiva lanseringar, och uppgifter som är tilldelade dig. Pausade lanseringar visas inte här.',
  },
  myDepartments: {
    title: 'Mina avdelningar',
    body: 'Välj vilka avdelningar du tillhör. Deras uppgifter visas här, och du kan starta och markera dem klara. Vem som gjorde det sparas i historiken.',
  },
  myWorkLate: {
    title: 'Försenat',
    body: 'Deadline har passerat och uppgiften är inte klar.',
  },
  myWorkAction: {
    title: 'Behöver åtgärd',
    body: 'Något behöver göras snart: deadline inom 7 dagar, prognosen är senare än planen eller ett beslut väntar.',
  },
  myWorkToday: {
    title: 'Idag',
    body: 'Deadline är idag.',
  },
  myWorkUpcoming: {
    title: 'Kommande',
    body: 'Deadline längre fram. Inget brådskar ännu.',
  },
  myWorkUndated: {
    title: 'Utan datum',
    body: 'Uppgiften har inget datum. Det är tillåtet och behöver inte åtgärdas bara för det.',
  },
  myWorkBlocked: {
    title: 'Blockerat',
    body: 'Ett hinder är registrerat. Uppgiften kan inte gå vidare förrän hindret är löst.',
  },
  myWorkWaiting: {
    title: 'Väntar',
    body: 'Ett godkännande har påbörjats men är inte klart för alla roller.',
  },
  myWorkComplete: {
    title: 'Markera klar',
    body: 'Sparar uppgiften som klar med dagens datum. Kräver den godkännande måste besluten vara registrerade först.',
  },
  myWorkAttention: {
    title: 'Uppmärksamhet',
    body: 'Påminnelser om dina uppgifter: blockerade, försenade, snart eller som väntar på ditt beslut. Den röda siffran på fliken räknar dem. Klicka för att öppna.',
  },
  headerStatus: {
    title: 'Status',
    body: 'Var lanseringen befinner sig i sin livscykel.',
    items: [
      { label: 'Utkast', text: 'Planeras och syns inte i Mitt arbete.' },
      { label: 'Aktiv', text: 'Pågår.' },
      { label: 'Pausad', text: 'Tillfälligt stoppad, men går att ändra.' },
      { label: 'Lanserad', text: 'Produkten är ute.' },
      { label: 'Avslutad eller avbruten', text: 'Kan inte längre ändras.' },
    ],
  },
  headerHealth: {
    title: 'Läge',
    body: 'Försenad om någon uppgift är sen eller blockerad. Risk om en prognos är senare än planen. Annars I fas.',
  },
  headerCountdown: {
    title: 'Nedräkning',
    body: 'Varje kedja visas med dagar kvar till sin avisering. Den veckan är vecka 0, och deadlines räknas bakåt från datumet.',
  },
  headerProgress: {
    title: 'Framdrift',
    body: 'Andel klara uppgifter. Hållpunkter är fasta kalenderdatum och räknas inte.',
  },
  headerActions: {
    title: 'Ägarens knappar',
    body: 'Bara lanseringsägaren ser de här knapparna.',
    items: [
      { label: 'Redigera', text: 'Namn, beskrivning, ägare och produkter.' },
      { label: 'Aktivera', text: 'Startar lanseringen. Uppgifterna syns i Mitt arbete.' },
      { label: 'Pausa', text: 'Markerar att arbetet står still.' },
      { label: 'Återuppta', text: 'Tar bort pausen.' },
      { label: 'Markera lanserad', text: 'Produkten är ute.' },
      { label: 'Avsluta', text: 'Arkiverar och blir skrivskyddad.' },
      { label: 'Avbryt lansering', text: 'Blir inte av. Orsaken sparas och den blir skrivskyddad.' },
    ],
  },
  headerTabs: {
    title: 'Flikar',
    body: 'Samma lansering, fyra sätt att se den.',
    items: [
      { label: 'Översikt', text: 'Läget i korthet.' },
      { label: 'Arbetslista', text: 'Alla uppgifter med filter.' },
      { label: 'Tidslinje', text: 'Uppgifterna i kalendern.' },
      { label: 'Historik', text: 'Vad som hänt och sparade veckolägen.' },
    ],
  },
  kpiOverdue: {
    title: 'Försenade',
    body: 'Uppgifter vars deadline har passerat utan att de är klara. Klicka för att se dem.',
  },
  kpiWeek: {
    title: 'Denna vecka',
    body: 'Uppgifter med deadline från idag till och med söndag. Klicka för att se dem.',
  },
  kpiBlocked: {
    title: 'Blockerade',
    body: 'Uppgifter med ett hinder som inte är löst. Klicka för att se dem.',
  },
  overviewNow: {
    title: 'Att göra nu',
    body: 'De uppgifter som behöver uppmärksamhet först: blockerat, försenat, risk och snart.',
  },
  overviewPhases: {
    title: 'Faser',
    body: 'Lanseringen har sex faser som kan pågå samtidigt. Klicka på en fas för att se dess uppgifter.',
  },
  overviewChains: {
    title: 'Kedjor',
    body: 'Varje kedja, till exempel ICA, har ett eget aviseringsdatum som är vecka 0. Öppna krav är uppgifter kedjan kräver som inte är klara.',
  },
  overviewLate: {
    title: 'Sena ändringar',
    body: 'Ändringar som kommer in efter att planen är satt, till exempel nytt underlag. Registrera dem så att historiken visar varför tidplanen ändrades.',
  },
  workFilters: {
    title: 'Snabbfilter',
    body: 'Visar ett urval av uppgifterna. Klicka en gång till för att visa alla.',
    items: [
      { label: 'Min avdelning', text: 'Dina avdelningar, eller uppgifter tilldelade dig.' },
      { label: 'Försenade', text: 'Deadline har passerat.' },
      { label: 'Denna vecka', text: 'Deadline senast på söndag.' },
      { label: 'Blockerade', text: 'Olöst hinder.' },
      { label: 'Utan datum', text: 'Har inget datum. Det är tillåtet.' },
    ],
  },
  workGroup: {
    title: 'Gruppera',
    body: 'Efter brådska lägger det mest akuta överst och samlar klara uppgifter. Efter fas behåller uppgifterna mallens ordning.',
  },
  workLegend: {
    title: 'Så läser du listan',
    body: 'Varje rad är en uppgift eller en hållpunkt.',
    items: [
      { label: 'Färgad kant', text: 'Läget: försenat, risk, snart, enligt plan eller klart.' },
      { label: 'Hållpunkt', text: 'Fast datum utan arbete. Bockas inte av.' },
      { label: 'Utanför omfattning', text: 'Ingår inte och räknas inte i framdriften.' },
      { label: 'Riskerar att bli sent', text: 'Prognosen är senare än planen.' },
    ],
  },
  workCount: {
    title: 'Antal',
    body: 'Uppgifter kräver arbete. Hållpunkter är fasta datum och räknas inte i framdriften.',
  },
  drawerDate: {
    title: 'Datum',
    body: 'Deadline är datumet i grundplanen. En prognos visas separat. Grundplanen ändras bara när ett planförslag fastställs under Historik.',
  },
  drawerActions: {
    title: 'Handlingar',
    body: 'Den som tillhör avdelningen, den som är tilldelad, eller lanseringsägaren kan använda dem.',
    items: [
      { label: 'Starta', text: 'Arbetet är påbörjat.' },
      { label: 'Markera klar', text: 'Arbetet är gjort. Kräver uppgiften godkännande måste besluten vara registrerade först.' },
      { label: 'Återöppna', text: 'Ångrar klar. Den tilldelade eller ägaren kan göra det, och en orsak krävs.' },
      { label: 'Registrera hinder', text: 'Något stoppar arbetet.' },
      { label: 'Lös hinder', text: 'Hindret är borta och arbetet kan fortsätta.' },
      { label: 'Registrera beslut', text: 'Godkänn eller avslå för din roll. Beslutet gäller den aktuella underlagsversionen.' },
    ],
  },
  drawerOwner: {
    title: 'Ägaråtgärder',
    body: 'Bara lanseringsägaren ser de här knapparna.',
    items: [
      { label: 'Ange väntat datum', text: 'När uppgiften väntas bli klar. Planens datum ändras inte. Ett senare datum ger risk.' },
      { label: 'Föreslå nytt datum', text: 'Nya datum för kedjan. Gäller först när förslaget godkänns under Historik.' },
      { label: 'Ta bort från planen', text: 'Tar bort uppgiften från framdriften, eller lägger tillbaka den, efter att förslaget godkänns.' },
      { label: 'Ny version av underlaget', text: 'Ny version av underlaget. Tidigare godkännanden gäller inte längre.' },
    ],
  },
  drawerDefinition: {
    title: 'Klardefinition',
    body: 'Vad som ska vara uppfyllt för att uppgiften räknas som klar, och vilka godkännanden som krävs.',
  },
  drawerFiles: {
    title: 'Bilagor',
    body: 'Filer som hör till uppgiften, till exempel ingrediensförteckningar och stansmallar. De syns också samlat under fliken Filer.',
  },
  filesTab: {
    title: 'Filer',
    body: 'Underlag som avdelningarna laddar upp. Mapparna är ämnen, inte versioner. Allt som laddas upp eller tas bort sparas i historiken.',
  },
  filesUpload: {
    title: 'Ladda upp',
    body: 'Välj mapp och bifoga en eller flera filer. Samma namn i samma mapp sparas som en ny fil med (2) i namnet.',
  },
  drawerChains: {
    title: 'Kedjekrav',
    body: 'Datum som varje kedja kräver, räknat från kedjans avisering. v. är veckonummer.',
  },
  drawerPlan: {
    title: 'Planhistorik',
    body: 'Alla versioner av uppgiftens planerade datum och det senaste väntade datumet.',
  },
  drawerHistory: {
    title: 'Historik',
    body: 'Allt som hänt med uppgiften: vem, vad och när.',
  },
  drawerTechnical: {
    title: 'Tekniska detaljer',
    body: 'Källinformation från mallen, för felsökning. Behövs inte i det dagliga arbetet.',
  },
  timelineFilter: {
    title: 'Visa',
    body: 'Alla uppgifter, bara de som inte är klara, eller bara din avdelning.',
  },
  timelineZoom: {
    title: 'Zoom',
    body: 'Vecka visar detaljer. Kvartal visar hela planen.',
  },
  timelineRelative: {
    title: 'Relativ vecka',
    body: 'Veckorna räknas mot vald kedjas avisering. v.0 är aviseringsveckan och −6 betyder sex veckor före.',
  },
  timelineLegend: {
    title: 'Förklaring',
    body: 'Punkten är deadline i grundplanen, färgad efter läge. Romben med streckad linje är prognosen. Det skuggade bandet är aviseringsveckan och den röda linjen är idag. ◂ och ▸ betyder att datumet ligger utanför det visade fönstret.',
  },
  timelinePhase: {
    title: 'Fasrad',
    body: 'Visar klara av totalt och antal sena. Strecken visar var uppgifterna ligger när fasen är hopfälld.',
  },
  timelineUndated: {
    title: 'Uppgifter utan datum',
    body: 'Kan inte placeras i kalendern förrän de fått ett datum. Öppna uppgiften och föreslå en ny grundplan.',
  },
  timelineTable: {
    title: 'Tabellvy',
    body: 'Samma innehåll som tabell, för skärmläsare och utskrift.',
  },
  historyEvents: {
    title: 'Händelser',
    body: 'Logg över alla ändringar i lanseringen, med vem och när.',
  },
  historySnapshots: {
    title: 'Veckoobservationer',
    body: 'En sparad bild av läget en viss vecka, så att plan och utfall kan jämföras i efterhand. En felaktig observation kan rättas med orsak.',
  },
  historyPlans: {
    title: 'Plan- och omfattningsversioner',
    body: 'Förslag på nya datum eller ändrad omfattning. Ett förslag gäller först när ägaren fastställer det, och då skapas en ny planversion.',
  },
  historyNotices: {
    title: 'Interna uppmärksamhetspunkter',
    body: 'Påminnelser som skapats automatiskt. De skickas inte utanför Athena.',
  },
  settingsFolders: {
    title: 'Mappar',
    body: 'Mapparna kopieras in när en ny lansering skapas. Där samlas filerna. En pågående lansering behåller sina egna mappar.',
  },
  settingsChains: {
    title: 'Kedjor',
    body: 'Kedjorna som går att välja när en lansering skapas. Stäng av Aktiv om kedjan inte ska erbjudas. En kedja som redan används kan bara stängas av.',
  },
  settingsRoles: {
    title: 'Roller',
    body: 'Personen fylls i på rollen när en ny lansering skapas. Den kan bytas i lanseringen. Redan skapade lanseringar ändras inte.',
  },
  settingsFiles: {
    title: 'Filregler',
    body: 'Vilka filtyper som får laddas upp och hur stor en fil får vara. Gäller direkt, även i befintliga lanseringar.',
  },
  settingsTime: {
    title: 'Tidsgränser',
    body: 'Gäller direkt i alla lanseringar.',
    items: [
      { label: 'Snart', text: 'Dagarna före deadline som räknas som snart.' },
    ],
  },
  settingsLog: {
    title: 'Ändringslogg',
    body: 'De senaste sparade ändringarna av mappar, kedjor, roller, filregler och tidsgränser.',
  },
  settingsCalendar: {
    title: 'Kedjekalender',
    body: 'Varje aviseringsvecka visas som ett eget band. Under det finns en rad per kedja och revidering, med kategorierna som märken. Välj Redigera för att välja kategorier och publicera. Lanseringar som redan finns behåller sitt datum.',
  },
  yearbook: {
    title: 'Årskalender',
    body: 'Varje kedja är ett blad. Klicka i en cell för att ändra den, eller markera flera rader och fyll i samma värde. Lägg till vecka, kategori och produkt som tomma rader. Namn och EAN hämtas från Athena, annars från Produktinformation Dabas eller Produktinfo Alkohol.',
  },
  productInfo: {
    title: 'Produktinformation Dabas',
    body: 'Här bestäms artikelnummer, namn, texter och EAN innan produkten läggs in i Dabas. Sök på nummer, EAN, namn eller leverantör. Utgångna produkter döljs tills du söker eller väljer Utgått. Fälten sparas när du lämnar dem, och varje ändring syns i historiken.',
  },
  productInfoReady: {
    title: 'Klar för Dabas',
    body: 'Räknar Produktnamn Dabas, kategori, EAN för konsumentförpackning, marknadsbudskap och sökord. Ett fält som är längre än gränsen räknas inte. Kopiera alla Dabas-fält kopierar dem i den ordningen.',
  },
  productInfoMissing: {
    title: 'Saknar uppgifter',
    body: 'Produkter som saknar något av fälten som krävs för Dabas. Klicka för att bara visa dem.',
  },
  productInfoComing: {
    title: 'Kommande',
    body: 'Produkter med status Kommande. Klicka för att bara visa dem.',
  },
  productInfoReadyCount: {
    title: 'Klara för Dabas',
    body: 'Produkter där alla fält som krävs för Dabas är ifyllda och inom längdgränsen. Klicka för att bara visa dem.',
  },
  productInfoWarnings: {
    title: 'Varningar',
    body: 'Produkter med dubblett, för långt fält eller fel kontrollsiffra på EAN. Klicka för att bara visa dem.',
  },
  alcoholInfo: {
    title: 'Produktinfo Alkohol',
    body: 'Vin, sprit, öl och alkoholfritt till katalog och hemsida. Sök på nummer, namn, producent, druva eller region. Fälten sparas när du lämnar dem och varje ändring syns i historiken. Årskalendern hämtar namn härifrån när numret inte finns i Athena.',
  },
  alcoholApproved: {
    title: 'Godkända',
    body: 'Produkter som är klara att använda. Klicka för att bara visa dem.',
  },
  alcoholComing: {
    title: 'Väntar på godkännande',
    body: 'Kommande produkter. Läs anteckningen, fyll i det som saknas och välj Godkänn på kortet.',
  },
  alcoholNotCurrent: {
    title: 'Inte aktuella',
    body: 'Produkter med läget Ej aktuell just nu, Kundunik eller Blir inte av. De ska oftast inte med i katalogen.',
  },
  alcoholProducers: {
    title: 'Producenter',
    body: 'Varje producent har en Beskrivning vingård som gäller alla dess produkter. Klicka för att se och ändra texterna.',
  },
  alcoholComplete: {
    title: 'Komplett',
    body: 'Räknar produktnamn (högst 35 tecken), land, kategori, förpackning, alkoholhalt, region, Beskrivning, Kortare beskrivning, Passar till och Beskrivning vingård.',
  },
  alcoholTexts: {
    title: 'Texter',
    body: 'Gör ny text skriver om texten i rutan med flikens prompt. Förslaget sparas först när du väljer Använd. Beskrivning ger också en ny kortare beskrivning. Historik visar tidigare texter och du kan ta tillbaka en.',
  },
  alcoholPrompts: {
    title: 'Promptinställningar',
    body: 'Prompterna som Gör ny text använder. Bara texten i rutan skickas med. Kortare beskrivning ska korta den nya långa texten till högst 300 tecken. En tom prompt stänger av sin knapp. Bara administratörer kan spara.',
  },
  alcoholLegend: {
    title: 'Färger',
    body: 'Pricken visar kategori. Etiketterna visar status och läge. Färgen står alltid tillsammans med text.',
  },
  yearbookSamples: {
    title: 'Varuprover',
    body: 'Siffran i parentes är summan för samma artikelnummer i samma aviseringsvecka, i alla kedjor. Klockan sätter en påminnelse till dig. Den dagen får fliken Årskalender en röd siffra. Kollegor ser den i en egen ruta som de kan dölja. Bocka av med Skickat eller ta bort datumet.',
  },
  yearbookColumns: {
    title: 'Kolumner',
    body: 'Välj vilka fält som visas. Status VCD och QLAB visas inte. Valet sparas bara för dig.',
  },
  yearbookIssues: {
    title: 'Markeringar',
    body: 'Gula markeringar betyder att raden behöver ses över. Dölj tomma rader gömmer rader utan artikelnummer och produktnamn.',
  },
  yearbookContacts: {
    title: 'Kundinformation',
    body: 'Välj mottagare per rad och ange antal varuprover för var och en. Varuprover visar summan. Med flera mottagare fälls raden ut med pilen. Kortikonen visar kontaktuppgifter och antal, och du bläddrar mellan korten. I Kundinformation söker och filtrerar du på kedja.',
  },
  settingsTemplate: {
    title: 'Processmall',
    body: 'Varje mall har ett namn. Spara skriver över den valda mallen. En ny mall blir aktiv först när du markerar den. Nya lanseringar använder den aktiva mallen. Lanseringar som redan finns behåller sin plan. När du sparar kan du föra över namn, roller och radordning till dem.',
  },
  editLaunch: {
    title: 'Lansering',
    body: 'Namn, beskrivning och ägare. Ägaren ansvarar för planen och kan ändra datum, omfattning och status.',
  },
  editProducts: {
    title: 'Produkter',
    body: 'Produkterna som hör till lanseringen. Artikelnummer är valfritt.',
  },
  editRoles: {
    title: 'Roller',
    body: 'Öppna uppgifter utan ansvarig, eller med rollens förra person, går till den nya. Klara uppgifter ändras inte. Tar du bort personen behålls ansvariga.',
  },
  editChains: {
    title: 'Kedjor',
    body: 'Aviseringsdatum är vecka 0. Sista kedjan kan inte tas bort. Utom i utkast krävs en orsak.',
  },
} satisfies Record<string, HelpText>;
